import { Controller } from "@hotwired/stimulus"

// Shared modal for creating new AccountingLookup records
// Can be triggered from any controller via custom events
// Dispatches 'lookup-created' event with the new lookup data
export default class extends Controller {
  static targets = [
    "modal", "title", "kodas", "pavadinimas", "tipasField", "tipas", "buhSaskaitaField", "buhSaskaita", "error", "submitButton"
  ]
  static values = {
    lookupType: String,
    createUrl: { type: String, default: "/accounting_lookups" },
    syncedTypes: Array
  }

  initialize() {
    this.boundHandleOpenRequest = this.handleOpenRequest.bind(this)
  }

  connect() {
    // Listen for open requests from other controllers
    document.addEventListener('open-lookup-create-modal', this.boundHandleOpenRequest)
  }

  disconnect() {
    document.removeEventListener('open-lookup-create-modal', this.boundHandleOpenRequest)
  }

  handleOpenRequest(event) {
    const { lookupType, prefillKodas, callerId } = event.detail
    this.callerId = callerId
    this.open(lookupType, prefillKodas)
  }

  open(lookupType, prefillKodas = '') {
    // Block creation for synced types
    if (this.syncedTypesValue.includes(lookupType)) {
      console.warn(`Cannot create ${lookupType} - synced from external system`)
      return
    }

    this.lookupTypeValue = lookupType

    // Update title based on lookup type
    const typeNames = {
      'preke': 'Prekė / Išlaida',
      'padalinys': 'Padalinys',
      'objektas': 'Objektas',
      'serija': 'Serija',
      'centras': 'Centras',
      'atskaitingas_asmuo': 'Atskaitingas asmuo',
      'sandelis': 'Sandėlis',
      'korespondencija': 'Korespondencija'
    }
    this.titleTarget.textContent = `Naujas: ${typeNames[lookupType] || lookupType}`

    // Show/hide tipas field for preke type
    if (this.hasTipasFieldTarget) {
      this.tipasFieldTarget.classList.toggle('hidden', lookupType !== 'preke')
    }

    // Show/hide buh_saskaita field for atskaitingas_asmuo type
    if (this.hasBuhSaskaitaFieldTarget) {
      this.buhSaskaitaFieldTarget.classList.toggle('hidden', lookupType !== 'atskaitingas_asmuo')
    }

    // Pre-fill kodas if provided
    this.kodasTarget.value = prefillKodas ? prefillKodas.toUpperCase() : ''
    this.pavadinimasTarget.value = ''
    if (this.hasTipasTarget) {
      this.tipasTarget.value = ''
    }
    if (this.hasBuhSaskaitaTarget) {
      this.buhSaskaitaTarget.value = ''
    }
    this.hideError()

    this.modalTarget.showModal()
    this.kodasTarget.focus()
  }

  close() {
    this.modalTarget.close()
  }

  closed() {
    this.callerId = null
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  async submit(event) {
    event.preventDefault()
    event.stopPropagation()

    const kodas = this.kodasTarget.value.trim()
    const pavadinimas = this.pavadinimasTarget.value.trim()
    const tipas = this.hasTipasTarget ? this.tipasTarget.value : ''
    const buhSaskaita = this.hasBuhSaskaitaTarget ? this.buhSaskaitaTarget.value.trim() : ''

    if (!kodas) {
      this.showError('Kodas yra privalomas')
      return
    }

    // Show loading state
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = 'Kuriama...'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const body = {
        accounting_lookup: {
          lookup_type: this.lookupTypeValue,
          kodas: kodas,
          pavadinimas: pavadinimas
        }
      }

      if (tipas) {
        body.accounting_lookup.tipas = parseInt(tipas)
      }

      if (buhSaskaita) {
        body.accounting_lookup.buh_saskaita = buhSaskaita
      }

      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success) {
        // Dispatch success event with lookup data
        document.dispatchEvent(new CustomEvent('lookup-created', {
          detail: {
            lookup: data.lookup,
            lookupType: this.lookupTypeValue,
            callerId: this.callerId
          }
        }))

        // Show success toast
        this.showSuccessToast(`"${data.lookup.kodas}" sukurtas`)

        this.close()
      } else {
        this.showError(data.errors?.join(', ') || 'Klaida kuriant įrašą')
      }
    } catch (error) {
      console.error('Error creating lookup:', error)
      this.showError('Klaida kuriant įrašą')
    } finally {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'Sukurti'
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError() {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }

  showSuccessToast(message) {
    const toast = document.createElement('div')
    toast.className = 'fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 z-[60]'
    toast.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5 text-green-400">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span class="text-sm font-medium">${this.escapeHtml(message)}</span>
    `
    document.body.appendChild(toast)
    setTimeout(() => {
      toast.classList.add('opacity-0', 'transition-opacity', 'duration-300')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
